<template>
  <ActivityHistoryTable
    :title="text.history.title"
    :subtitle="text.history.subtitle"
    :loading="loading"
    :rows="rows"
    :loading-text="text.history.loading"
    :empty-text="text.history.empty"
    :headers="text.history.table.headers"
    :row-fallback="text.history.fallback"
  />
</template>

<script>
import customersService from '@/services/customers';
import { extractCollection } from '@/utils/dataNormalization';
import { formatDateTimeShort } from '@/utils/datePresentation';
import ActivityHistoryTable from '@/components/common/ActivityHistoryTable.vue';
import { useDomainText } from '@/composables/text/useDomainText';

const { catalog: text } = useDomainText('customers');

export default {
  name: 'CustomerDetailHistoryTab',
  components: {
    ActivityHistoryTable,
  },
  props: {
    customer: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      text,
      loading: false,
      rows: [],
      requestToken: 0,
    };
  },
  watch: {
    customerId: {
      immediate: true,
      handler() {
        this.loadHistory();
      },
    },
  },
  computed: {
    customerId() {
      return Number(this.customer?.id || 0);
    },
  },
  methods: {
    async loadHistory() {
      const customerId = Number(this.customerId || 0);
      if (customerId <= 0) {
        this.rows = [];
        return;
      }

      this.loading = true;
      const token = this.requestToken + 1;
      this.requestToken = token;

      try {
        const response = await customersService.activity(customerId, { per_page: 200 });
        if (token !== this.requestToken) {
          return;
        }

        this.rows = extractCollection(response)
          .map((row) => this.mapHistoryRow(row))
          .filter((row) => row !== null);
      } catch (error) {
        if (token === this.requestToken) {
          this.rows = [];
        }
      } finally {
        if (token === this.requestToken) {
          this.loading = false;
        }
      }
    },
    mapHistoryRow(raw) {
      if (!raw || typeof raw !== 'object') {
        return null;
      }

      const moduleCode = String(raw.module || '').trim().toLowerCase();
      const eventType = String(raw.event_type || '').trim().toLowerCase();
      const dateSource = raw.happened_at || raw.created_at || raw.date_label;

      return {
        id: Number(raw.id || 0) || `${moduleCode}-${eventType}-${Date.now()}`,
        date_label: formatDateTimeShort(dateSource, { fallback: text.history.fallback.date }),
        module_label: String(raw.module_label || '').trim() || this.toTitle(moduleCode) || text.history.fallback.module,
        event_label: String(raw.event_label || '').trim() || this.toTitle(eventType) || text.history.fallback.event,
        description_label: String(raw.description_label || '').trim() || String(raw.description || raw.title || text.history.fallback.description).trim(),
        actor_label: String(raw.actor_label || raw.actor_name || text.history.fallback.actor).trim(),
      };
    },
    toTitle(value) {
      const normalized = String(value || '').trim().replace(/[_-]+/g, ' ');
      if (normalized === '') {
        return '';
      }

      return normalized.charAt(0).toUpperCase() + normalized.slice(1);
    },
  },
};
</script>
